import { useMemo, useState } from "react";
import { Alert, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import { type GridColDef } from "@mui/x-data-grid-pro";
import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { FeedGrid, type IProps } from "./AllMattersDetailFeedGrid";
import { AllMattersActionSection } from "./AllMattersActionSection";
import { AllMattersV2Excel } from "./AllMattersV2Excel";
import { AllMattersV2Panel } from "./AllMattersV2Panel";
import { type AnalyzedRealEstate } from "../types";
import { useV2Detail } from "../hooks/useV2Detail";
import { v2DetailsUrl, fetchV2Details, type V2DetailList } from "../api/v2";

// Mounted only while allmatters-v2 is on. No v2 requests or row handlers exist in v1.
export const AllMattersV2FeedGrid = (props: IProps): JSX.Element => {
  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();
  const [selected, setSelected] = useState<AnalyzedRealEstate | null>(null);
  // Lives here so the layout survives switching rows (the panel remounts).
  const [sideBySide, setSideBySide] = useState(false);
  const baseUrl =
    props.requestId != null ? v2DetailsUrl(props.requestId) : null;
  const {
    data: statuses,
    error: listError,
    mutate: refreshStatuses,
  } = useSWR<V2DetailList, Error>(
    baseUrl,
    async (url: string) => await fetchV2Details(apiClient, url),
    { refreshInterval: 60000 }
  );
  const { panel, clear } = useV2Detail(
    baseUrl && selected ? `${baseUrl}/${selected.id}` : null
  );
  const column = useMemo<GridColDef>(() => {
    const byId = new Map(statuses?.details.map((item) => [item.id, item]));
    return {
      field: "v2Status",
      headerName: "解析結果",
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const status = byId.get(Number(params.id));
        return (
          <Chip
            size="small"
            variant="outlined"
            label={
              listError
                ? "取得失敗"
                : !statuses
                ? "確認中"
                : status?.v2Status === "failed"
                ? status.hasResult
                  ? "失敗（前回の結果あり）"
                  : "失敗"
                : status?.hasResult
                ? "結果あり"
                : status?.v2Status === "succeeded"
                ? "結果なし"
                : "未完了"
            }
            color={status?.v2Status === "failed" ? "warning" : "default"}
          />
        );
      },
    };
  }, [statuses, listError]);

  return (
    <>
      <FeedGrid
        {...props}
        onOpenRow={setSelected}
        v2StatusColumn={column}
        v2Toolbar={
          props.requestId != null ? (
            <AllMattersActionSection
              title="詳細な解析結果"
              description="この依頼を閲覧できる方は、同じファイルをダウンロードできます。"
            >
              <Stack spacing={2} divider={<Divider flexItem />}>
                <AllMattersV2Excel
                  key={props.requestId}
                  requestId={props.requestId}
                />
                <AllMattersV2Excel
                  key={`${props.requestId}:property-detail`}
                  requestId={props.requestId}
                  includePropertyDetail
                  isReadOnly={isReadOnly}
                  isPropertyDetailOptionCredit={
                    props.isPropertyDetailOptionCredit
                  }
                  onDownloadComplete={props.onDownloadComplete}
                />
              </Stack>
            </AllMattersActionSection>
          ) : null
        }
        v2Caption={
          <>
            <Typography variant="body2" color="text.secondary">
              行をクリックするか Enter キーを押すと、登記の PDF
              と解析結果を確認できます。
            </Typography>
            {listError && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    onClick={() => {
                      void refreshStatuses();
                    }}
                  >
                    再読み込み
                  </Button>
                }
              >
                解析結果の状況を取得できませんでした。行をクリックすると詳細を確認できます。
              </Alert>
            )}
          </>
        }
      />
      {selected && (
        <AllMattersV2Panel
          key={`${props.requestId ?? 0}:${selected.id}`}
          property={{
            prefecture: selected.prefecture,
            location: selected.location,
            number:
              (selected.realEstateType === "土地"
                ? selected.chiban
                : selected.kaokuNumber) ?? selected.chibanKaokuNumber,
            type: selected.realEstateType,
          }}
          {...panel}
          sideBySide={sideBySide}
          onSideBySideChange={setSideBySide}
          onClose={() => {
            setSelected(null);
            clear();
          }}
        />
      )}
    </>
  );
};
